import { Media } from '@/components/Media'
import { Link } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { StatTile } from '@/components/StatTile'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor } from '@/lib/money'
import { VENUES } from './wellness-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { BRAND } from '@/brand/brand'

const GYMS = VENUES.filter((venue) => venue.kind === 'gym')

const WHY = [
  {
    icon: 'event_busy',
    title: 'No contract, no cancellation call',
    body: 'A day or a week at a time. Nothing renews by itself and there is nothing to cancel when you fly home.',
  },
  {
    icon: 'public',
    title: 'The same pass in four cities',
    body: 'Mexico City, Bogotá, São Paulo and Santa Cruz. Your pass follows the corridor you travel.',
  },
  {
    icon: 'payments',
    title: 'Charged in dollars',
    body: `Paid from your ${BRAND.name} balance at the real rate, with no foreign transaction fee on top.`,
  },
]

export function GymScreen() {
  return (
    <>
      <PageHeader
        title="Gyms"
        subtitle="A day pass where you are, without signing up for a year. 5% back on every one."
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="arrow_back" to="/wellness">
              All of Wellness
            </Button>
            <Button variant="primary" size="md" iconStart="event" to="/transactions">
              My passes
            </Button>
          </>
        }
      />

      <BentoGrid>
        {[
          { label: 'Gyms nearby', value: '86', note: 'Within 5 km of you' },
          { label: 'Day pass from', value: '$6.00', note: 'No contract' },
          { label: 'Cashback', value: '5%', note: 'Paid into your USD account' },
          { label: 'Cities', value: '4', note: 'Across your corridors' },
        ].map((stat) => (
          <StatTile key={stat.label} stat={stat} />
        ))}

        <BentoSpan span={3}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Gyms you can walk into
              </Text>
              <Pill tone="neutral">{GYMS.length}</Pill>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {GYMS.map((gym) => {
                const dayPass = gym.services[0]
                return (
                  <Link
                    key={gym.slug}
                    to={`/wellness/${gym.slug}`}
                    className={checkout.line}
                    style={{ width: '100%' }}
                  >
                    <Media src={gym.image} alt="" width={64} ratio={1} radius="md" icon="exercise" />
                    <Stack grow gap={0} align="start">
                      <Text variant="caption" weight="semibold">
                        {gym.name}
                      </Text>
                      <Text variant="caption" tone="secondary" truncate>
                        {gym.city} · {gym.detail}
                      </Text>
                    </Stack>
                    <Pill tone="neutral">{gym.score}</Pill>
                    <Stack align="end" gap={0} style={{ width: '6.5rem' }}>
                      <Text variant="caption" weight="semibold" numeric>
                        {formatMoney(fromMajor(dayPass?.price ?? 0, 'USD'))}
                      </Text>
                      <Text variant="eyebrow" tone="secondary">
                        day pass
                      </Text>
                    </Stack>
                  </Link>
                )
              })}
            </Stack>
          </Card>
        </BentoSpan>

        <Card tone="dark">
          <Text variant="caption" weight="semibold" tone="onDark">
            What a pass costs
          </Text>
          <Stack gap={0} className="pb-mt-3">
            {[
              ['Day pass', 'from $6'],
              ['Week pass', 'from $19'],
              ['Month · no contract', 'from $34'],
              ['Joining fee', 'None'],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 3 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}
          </Stack>
          <Card.Footer>
            <Text variant="eyebrow" tone="onDarkMuted">
              Cashback lands in your USD account after the visit, not as points.
            </Text>
          </Card.Footer>
        </Card>

        {WHY.map((item) => (
          <BentoSpan key={item.title} span={2}>
            <Card tone="light">
              <Stack direction="row" gap={3} align="start">
                <Icon name={item.icon} size={22} />
                <Stack gap={1}>
                  <Text variant="caption" weight="semibold">
                    {item.title}
                  </Text>
                  <Text variant="caption" tone="secondary">
                    {item.body}
                  </Text>
                </Stack>
              </Stack>
            </Card>
          </BentoSpan>
        ))}
      </BentoGrid>
    </>
  )
}
